/**
 * Persisted update-flow state (post-reload sync phase).
 * Shared so useOfflineSync can detect resume without importing React context.
 */

export const UPDATE_FLOW_STATE_STORAGE_KEY = 'pwa_update_flow_state';

const PERSISTED_STATE_TTL_MS = 5 * 60 * 1000;

export interface UpdateFlowPersistedState {
  phase: 'syncing';
  fromVersion: string;
  toVersion: string;
  timestamp: number;
}

export function readPersistedUpdateFlowState(): UpdateFlowPersistedState | null {
  try {
    const raw = localStorage.getItem(UPDATE_FLOW_STATE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UpdateFlowPersistedState;
    if (parsed.phase !== 'syncing') return null;
    if (Date.now() - (parsed.timestamp ?? 0) > PERSISTED_STATE_TTL_MS) {
      localStorage.removeItem(UPDATE_FLOW_STATE_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** True when app reloaded after an update and must finish sync before closing the update overlay. */
export function isPostRestartUpdateSyncPending(): boolean {
  return readPersistedUpdateFlowState() !== null;
}

export function writePersistedUpdateFlowSyncing(fromVersion: string, toVersion: string): void {
  const state: UpdateFlowPersistedState = {
    phase: 'syncing',
    fromVersion,
    toVersion,
    timestamp: Date.now(),
  };
  try {
    localStorage.setItem(UPDATE_FLOW_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function clearPersistedUpdateFlowState(): void {
  try {
    localStorage.removeItem(UPDATE_FLOW_STATE_STORAGE_KEY);
  } catch {
    // ignore
  }
}
